import React, { useState, useEffect } from "react";
import axios from "axios";
import SideBar from "../../components/SideBar";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import TableIMG from "../../assets/image/tableimg.png";
import { Link } from "react-router-dom";

function Order() {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const [Data, setData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedTables, setSelectedTables] = useState([]);
  const [isMerging, setIsMerging] = useState(false);
  const [isUnmerging, setIsUnmerging] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/Tables/order_table`);
      if (response.status === 200) {
        setData(response.data.items || []);
        setError("");
      } else {
        setError(response.data.message || "Failed to fetch tables.");
      }
    } catch (err) {
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "available":
        return "green";
      case "occupied":
        return "red";
      default:
        return "gray";
    }
  };

  const availableCount = Data.filter(
    (item) => item.tables_status === "available"
  ).length;
  const occupiedCount = Data.filter(
    (item) => item.tables_status === "occupied"
  ).length;

  const handleTableSelection = (id) => {
    const table = Data.find((t) => t.id === id);
    if (!table) return;

    if (isMerging && table.tables_merge_status !== "Yes") {
      setSelectedTables((prev) =>
        prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id]
      );
    }

    if (isUnmerging && table.tables_merge_status === "Yes") {
      const groupSlug = table.primary_slug;
      const groupTables = Data.filter(
        (t) => t.primary_slug === groupSlug && t.tables_merge_status === "Yes"
      );
      const selectedValid = groupTables.some((t) =>
        selectedTables.includes(t.id)
      );

      if (selectedTables.length === 0 || selectedValid) {
        setSelectedTables((prev) =>
          prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id]
        );
      }
    }
  };

  const getMergedTableSlug = () => {
    const selected = Data.filter((item) => selectedTables.includes(item.id));
    const occupiedTable = selected.find(
      (item) => item.tables_status === "occupied"
    );
    if (occupiedTable) return occupiedTable.slug;
    return selected.reduce(
      (min, item) => (item.id < min.id ? item : min),
      selected[0]
    )?.slug;
  };

  const handleMergeTables = () => {
    if (selectedTables.length < 2) {
      alert("Please select at least two tables to merge.");
      return;
    }

    const selected = Data.filter((item) => selectedTables.includes(item.id));
    const allOccupied = selected.every(
      (item) => item.tables_status === "occupied"
    );

    if (allOccupied) {
      alert("Cannot merge. All selected tables are occupied.");
      return;
    }

    axios
      .post(`${API_BASE_URL}/Tables/merge_tables`, {
        tables: selectedTables,
        slug: getMergedTableSlug(),
      })
      .then((res) => {
        alert("Tables merged successfully.");
        fetchData();
        setSelectedTables([]);
        setIsMerging(false);
      })
      .catch((err) => {
        const msg = err.response?.data?.status_message || "Merge failed.";
        alert(msg);
      });
  };

  const handleUnmergeTablesBySelection = () => {
    if (selectedTables.length < 2) {
      alert("Select at least two tables to unmerge.");
      return;
    }

    const selected = Data.filter((item) => selectedTables.includes(item.id));
    const slug = selected[0]?.primary_slug;
    const allSameGroup = selected.every((item) => item.primary_slug === slug);

    if (!slug || !allSameGroup) {
      alert("Selected tables must be from the same merged group.");
      return;
    }

    axios
      .post(`${API_BASE_URL}/Tables/unmerge_tables`, { slug })
      .then(() => {
        alert("Tables unmerged successfully.");
        fetchData();
        setSelectedTables([]);
        setIsUnmerging(false);
      })
      .catch(() => alert("Failed to unmerge tables."));
  };

  return (
    <>
      <SideBar />
      <Header />
      <div className="MainContainer">
        <div className="OrderHeader">
          <button onClick={() => setFilterStatus("")}>All Tables</button>
          <button onClick={() => setFilterStatus("available")}>
            Available ({availableCount})
          </button>
          <button onClick={() => setFilterStatus("occupied")}>
            Occupied ({occupiedCount})
          </button>
          <button
            onClick={() => {
              setIsMerging(!isMerging);
              setIsUnmerging(false);
              setSelectedTables([]);
            }}
          >
            {isMerging ? "Cancel Merge" : "Merge Tables"}
          </button>
          {isMerging && (
            <button
              onClick={handleMergeTables}
              disabled={selectedTables.length < 2}
            >
              Confirm Merge
            </button>
          )}
          <button
            onClick={() => {
              setIsUnmerging(!isUnmerging);
              setIsMerging(false);
              setSelectedTables([]);
            }}
          >
            {isUnmerging ? "Cancel Unmerge" : "Unmerge Tables"}
          </button>
          {isUnmerging && (
            <button
              onClick={handleUnmergeTablesBySelection}
              disabled={selectedTables.length < 2}
            >
              Confirm Unmerge
            </button>
          )}
        </div>

        {loading && <p>Loading...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}

        {Data.length > 0 ? (
          <div className="OrderContainer">
            {Data.filter((item) =>
              filterStatus ? item.tables_status === filterStatus : true
            ).map((item) => (
              <div
                className={`TableBox ${
                  item.tables_merge_status === "Yes" ? "Merged" : ""
                }`}
                key={item.id}
                onClick={() => {
                  if (isMerging || isUnmerging) handleTableSelection(item.id);
                }}
                style={{
                  border: selectedTables.includes(item.id)
                    ? "2px solid blue"
                    : "1px solid transparent",
                  opacity:
                    isUnmerging &&
                    selectedTables.length > 0 &&
                    item.primary_slug !==
                      Data.find((t) => t.id === selectedTables[0])?.primary_slug
                      ? 0.4
                      : 1,
                }}
              >
                {isMerging || isUnmerging ? (
                  <div>
                    <img src={TableIMG} alt="tables" />
                    <p
                      style={{
                        backgroundColor: getStatusColor(item.tables_status),
                      }}
                    >
                      {item.table_code}
                    </p>
                  </div>
                ) : (
                  <Link
                    to={
                      item.tables_merge_status === "Yes"
                        ? item.primary_slug || item.slug
                        : item.slug
                    }
                  >
                    <img src={TableIMG} alt="tables" />
                    <p
                      style={{
                        backgroundColor: getStatusColor(item.tables_status),
                      }}
                    >
                      {item.table_code}
                    </p>
                  </Link>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p>No data available.</p>
        )}
      </div>
      <Footer />
    </>
  );
}

export default Order;
